/* eslint-disable max-lines, max-lines-per-function, max-statements, react/jsx-no-bind */

import { Icon, InputText, type Option } from "@bluframe/blublocks"
import useToggle from "@bluframe/grapple/useToggle"
import type { SocialsHandleRelationshipState } from "@bluframe/mightychroma-mightytool-frontend-types"
import Button from "components/buttons/Button"
import { type ChangeEventHandler, useMemo } from "react"

import type { ComponentProps } from "."
import {
  Filters,
  FiltersActions,
  FiltersGrid,
  FiltersSelect,
  FiltersToggleButton
} from "./styled"

type Props = Pick<
  ComponentProps,
  "filters" | "onClearFilters" | "onFiltersChange"
>

const relationshipOptions: Option[] = [
  { label: "Any relationship", value: "" },
  { label: "Follower", value: "FOLLOWER" },
  { label: "Following", value: "FOLLOWING" },
  { label: "Mutual", value: "MUTUAL" },
  { label: "None", value: "NONE" }
]

const ignoredOptions: Option[] = [
  { label: "Any ignored state", value: "" },
  { label: "Ignored only", value: "true" },
  { label: "Not ignored", value: "false" }
]

const followsYouOptions: Option[] = [
  { label: "Any follow state", value: "" },
  { label: "Follows you", value: "true" },
  { label: "Doesn't follow you", value: "false" }
]

const isRelationshipState = (
  value: unknown
): value is SocialsHandleRelationshipState =>
  value === "FOLLOWER" ||
  value === "FOLLOWING" ||
  value === "MUTUAL" ||
  value === "NONE"

const parseOptionalNumber = (value: string): number | undefined => {
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const parseOptionalInteger = (value: string): number | undefined => {
  if (!value) return undefined
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

const parseTopicTags = (value: string): string[] | undefined => {
  const tags = value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean)
  return tags.length ? tags : undefined
}

const formatTopicTags = (tags: string[] | null | undefined) =>
  tags?.length ? tags.join(", ") : ""

const formatNumberInput = (value: number | null | undefined) =>
  value === undefined || value === null ? "" : String(value)

const getIgnoredSelectValue = (value: boolean | null | undefined) => {
  if (value === undefined || value === null) return ""
  return value ? "true" : "false"
}

const getFollowsYouSelectValue = (value: boolean | null | undefined) => {
  if (value === undefined || value === null) return ""
  return value ? "true" : "false"
}

const getRelationshipSelectValue = (
  value: SocialsHandleRelationshipState | null | undefined
) => value ?? ""

const parseIgnoredSelectValue = (value: unknown): boolean | undefined => {
  if (value === null || value === "") return undefined
  return String(value) === "true"
}

const isNonEmptyFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) return value.length > 0
  return value !== ""
}

const ProspectQueueFilters = ({
  filters,
  onClearFilters,
  onFiltersChange
}: Props) => {
  const [isExpanded, toggleIsExpanded] = useToggle(false)

  const activeFiltersCount = useMemo(
    () =>
      Object.values(filters).filter((value) => isNonEmptyFilterValue(value))
        .length,
    [filters]
  )

  const handleToggleFilters = () => toggleIsExpanded()

  const handleScoreMinChange: ChangeEventHandler<HTMLInputElement> = (event) =>
    onFiltersChange({
      ...filters,
      scoreMin: parseOptionalNumber(event.target.value)
    })

  const handleScoreMaxChange: ChangeEventHandler<HTMLInputElement> = (event) =>
    onFiltersChange({
      ...filters,
      scoreMax: parseOptionalNumber(event.target.value)
    })

  const handleRecencyDaysChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) =>
    onFiltersChange({
      ...filters,
      recencyDays: parseOptionalInteger(event.target.value)
    })

  const handleAccountCreatedSinceDaysChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      accountCreatedSinceDays: parseOptionalInteger(event.target.value)
    })

  const handleLatestInteractedDaysMinChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      latestInteractedDaysMin: parseOptionalInteger(event.target.value)
    })

  const handleTopicTagsChange: ChangeEventHandler<HTMLInputElement> = (event) =>
    onFiltersChange({
      ...filters,
      topicTags: parseTopicTags(event.target.value)
    })

  const handleFollowBackRatioMinChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) =>
    onFiltersChange({
      ...filters,
      followBackRatioMin: event.target.value
        ? parseOptionalNumber(event.target.value)
        : null
    })

  const handleFollowBackRatioMaxChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) =>
    onFiltersChange({
      ...filters,
      followBackRatioMax: parseOptionalNumber(event.target.value)
    })

  const handleFollowerCountMinChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) =>
    onFiltersChange({
      ...filters,
      followerCountMin: parseOptionalInteger(event.target.value)
    })

  const handleFollowingCountMinChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) =>
    onFiltersChange({
      ...filters,
      followingCountMin: parseOptionalInteger(event.target.value)
    })

  const handleOutboundAmplificationMinChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      outboundAmplificationMin: parseOptionalNumber(event.target.value)
    })

  const handleOutboundAmplificationMaxChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      outboundAmplificationMax: parseOptionalNumber(event.target.value)
    })

  const handleOutboundReshareRateMinChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      outboundReshareRateMin: parseOptionalNumber(event.target.value)
    })

  const handleOutboundReshareRateMaxChange: ChangeEventHandler<
    HTMLInputElement
  > = (event) =>
    onFiltersChange({
      ...filters,
      outboundReshareRateMax: parseOptionalNumber(event.target.value)
    })

  const handleRelationshipChange = (value: unknown) =>
    onFiltersChange({
      ...filters,
      ...(isRelationshipState(value) ? { followsYou: undefined } : {}),
      relationshipState: isRelationshipState(value) ? value : undefined
    })

  const handleFollowsYouChange = (value: unknown) =>
    onFiltersChange({
      ...filters,
      ...(typeof parseIgnoredSelectValue(value) === "boolean"
        ? { relationshipState: undefined }
        : {}),
      followsYou: parseIgnoredSelectValue(value)
    })

  const handleIgnoredChange = (value: unknown) =>
    onFiltersChange({ ...filters, isIgnored: parseIgnoredSelectValue(value) })

  return (
    <Filters aria-label="Filters" role="region">
      <FiltersToggleButton
        expanded
        iconEnd={<Icon name={isExpanded ? "IoChevronUp" : "IoChevronDown"} />}
        label={
          activeFiltersCount ? `Filters (${activeFiltersCount})` : "Filters"
        }
        onClick={handleToggleFilters}
        transparent
      />

      {isExpanded ? (
        <>
          <FiltersGrid>
            <InputText
              inputId="socials-prospect-queue-filter-score-min"
              label="Score min"
              name="scoreMin"
              onChange={handleScoreMinChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.scoreMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-score-max"
              label="Score max"
              name="scoreMax"
              onChange={handleScoreMaxChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.scoreMax)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-recency-days"
              label="Recency (days)"
              name="recencyDays"
              onChange={handleRecencyDaysChange}
              step="1"
              type="number"
              value={formatNumberInput(filters.recencyDays)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-topic-tags"
              label="Topic tags (comma-separated)"
              name="topicTags"
              onChange={handleTopicTagsChange}
              value={formatTopicTags(filters.topicTags)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-follow-back-min"
              label="Follow-back ratio min"
              name="followBackRatioMin"
              onChange={handleFollowBackRatioMinChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.followBackRatioMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-follow-back-max"
              label="Follow-back ratio max"
              name="followBackRatioMax"
              onChange={handleFollowBackRatioMaxChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.followBackRatioMax)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-followers-min"
              label="Followers min"
              name="followerCountMin"
              onChange={handleFollowerCountMinChange}
              step="1"
              type="number"
              value={formatNumberInput(filters.followerCountMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-following-min"
              label="Following min"
              name="followingCountMin"
              onChange={handleFollowingCountMinChange}
              step="1"
              type="number"
              value={formatNumberInput(filters.followingCountMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-outbound-amp-min"
              label="Outbound amplification min"
              name="outboundAmplificationMin"
              onChange={handleOutboundAmplificationMinChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.outboundAmplificationMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-outbound-amp-max"
              label="Outbound amplification max"
              name="outboundAmplificationMax"
              onChange={handleOutboundAmplificationMaxChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.outboundAmplificationMax)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-outbound-reshare-min"
              label="Outbound reshare rate min"
              name="outboundReshareRateMin"
              onChange={handleOutboundReshareRateMinChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.outboundReshareRateMin)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-outbound-reshare-max"
              label="Outbound reshare rate max"
              name="outboundReshareRateMax"
              onChange={handleOutboundReshareRateMaxChange}
              step="0.01"
              type="number"
              value={formatNumberInput(filters.outboundReshareRateMax)}
            />

            <FiltersSelect
              label="Relationship"
              name="relationshipState"
              onChange={handleRelationshipChange}
              options={relationshipOptions}
              value={getRelationshipSelectValue(filters.relationshipState)}
            />

            <FiltersSelect
              label="Follows you"
              name="followsYou"
              onChange={handleFollowsYouChange}
              options={followsYouOptions}
              value={getFollowsYouSelectValue(filters.followsYou)}
            />

            <FiltersSelect
              label="Ignored"
              name="isIgnored"
              onChange={handleIgnoredChange}
              options={ignoredOptions}
              value={getIgnoredSelectValue(filters.isIgnored)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-account-created-since-days"
              label="Account created in last (days)"
              name="accountCreatedSinceDays"
              onChange={handleAccountCreatedSinceDaysChange}
              step="1"
              type="number"
              value={formatNumberInput(filters.accountCreatedSinceDays)}
            />

            <InputText
              inputId="socials-prospect-queue-filter-latest-interacted-days-min"
              label="Haven't interacted in (days)"
              name="latestInteractedDaysMin"
              onChange={handleLatestInteractedDaysMinChange}
              step="1"
              type="number"
              value={formatNumberInput(filters.latestInteractedDaysMin)}
            />
          </FiltersGrid>

          <FiltersActions>
            <Button label="Clear filters" onClick={onClearFilters} outlined />
          </FiltersActions>
        </>
      ) : null}
    </Filters>
  )
}

export default ProspectQueueFilters
